import './shim';
import './console-overrides';

import { PipelineConfig, PreloadConfig } from '@webex/ladon-ts';

import { LadonGenerator } from './ladon-generator';
import { modelManager } from './model-manager';


const MAX_QUEUE_SIZE = 3;
const queue: { image: Uint8ClampedArray; timestamp: number }[] = [];

let preloadPromise: Promise<void> | null = null;
let generator: LadonGenerator;
let processing = false;


async function processQueue() {
    if (processing) return;
    processing = true;
    while (queue.length > 0) {
        const { image, timestamp } = queue.shift()!;
        await handleGenerate(image, timestamp);
    }
    setTimeout(() => {
        processing = false;
    }, 0);
}


// Message handler for worker
self.addEventListener('message', async (event) => {
    const { type, config, image, timestamp } = event.data;

    try {
        switch (type) {
            case 'preload':
                if (config) {
                    preloadPromise = handlePreload(config as PreloadConfig);
                    await preloadPromise;
                }
                break;

            case 'init':
                if (config) {
                    // Wait for preload to complete before initializing
                    if (preloadPromise) {
                        await preloadPromise;
                    }
                    await handleInit(config as PipelineConfig);
                }
                break;

            case 'generate':
                if (image) {
                    if (queue.length >= MAX_QUEUE_SIZE) {
                        // Drop the oldest frame and notify main thread
                        const dropped = queue.shift();
                        if (dropped) {
                            postMessage({
                                facesAndLandmarks: null,
                                gesture: null,
                                mask: null,
                                motion: null,
                                timestamp: dropped.timestamp,
                                status: 'dropped',
                                warm: false,
                            });
                        }
                    }
                    queue.push({ image, timestamp });
                    processQueue();
                }
                break;

            case 'destroy':
                await handleDestroy();
                break;

            default:
                console.warn(
                    `[ladon-ts] Worker generator recieved unknown message: ${JSON.stringify(
                        event.data
                    )}`
                );
        }
    } catch (error) {
        console.error(`[ladon-ts] Worker generator error handling message: ${error}`);
        postMessage({ status: 'error', error: error.message });
    }
});

// Handle preload event
async function handlePreload(config: PreloadConfig): Promise<void> {
    await modelManager.loadModel(config);
    postMessage({ status: 'preloaded' });
}

// Handle initialization
async function handleInit(config: PipelineConfig): Promise<void> {
    generator = new LadonGenerator(config);
    await generator.load();
    postMessage({ status: 'initialized' });
}

// Handle generate event with SharedArrayBuffer support
async function handleGenerate(image: Uint8ClampedArray | null, timestamp: number): Promise<void> {
    if (!generator || !generator.isLoaded()) {
        console.warn('[ladon-ts] Generator is not initialized');
        return;
    }

    if (!image) {
        console.warn('[ladon-ts] No valid input provided for generation');
        return;
    }

    // Run generator
    const maskResult = await generator.getLadonOutput(image, timestamp);

    postMessage({
        facesAndLandmarks: maskResult.facesAndLandmarks,
        gesture: maskResult.gesture,
        mask: maskResult.data, // Transfer mask directly if TypedArray
        motion: maskResult.motion,
        timestamp: maskResult.timestamp,
        status: 'generated',
        warm: maskResult.warm,
    });
}

// Handle destroy event
async function handleDestroy(): Promise<void> {
    if (generator) {
        await generator.destroy();
    } else {
        console.warn('[ladon-ts] No generator to destroy');
    }
    postMessage({ status: 'destroyed' });
}
